import React, { useState } from "react";
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import {
  CheckCircle,
  TrendingUp,
  DollarSign,
  Video,
  Zap,
  ArrowRight,
  Star,
  Menu,
  X,
} from "lucide-react";

export default function ViralLandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("weekly");

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-white font-sans">
      {/* Header with improved navigation */}
      <header className="py-6 px-6 lg:px-12 flex justify-between items-center border-b border-slate-800/50 backdrop-blur-lg sticky top-0 z-50 bg-slate-950/80">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-slate-800/40 rounded-xl shadow-lg">
            <Zap className="h-7 w-7 text-yellow-400" />
          </div>
          <span className="text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
            ViralShortVids
          </span>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <a
            href="#features"
            className="text-slate-300 hover:text-white transition-colors duration-200"
          >
            Features
          </a>
          <a
            href="#how-it-works"
            className="text-slate-300 hover:text-white transition-colors duration-200"
          >
            How It Works
          </a>
          <a
            href="#testimonials"
            className="text-slate-300 hover:text-white transition-colors duration-200"
          >
            Testimonials
          </a>
          <a
            href="#pricing"
            className="text-slate-300 hover:text-white transition-colors duration-200"
          >
            Pricing
          </a>
          <Button
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg shadow-yellow-500/20"
            onClick={(e: any) => {
              // Add analytics
              window.location.href = "https://substack.com/@viralshortvids";
            }}
          >
            Join Newsletter
          </Button>
        </nav>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-slate-950/95 pt-20 px-6 flex flex-col md:hidden animate-in fade-in duration-200">
          <nav className="flex flex-col space-y-6 text-lg">
            <a
              href="#features"
              className="py-2 border-b border-slate-800/50 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className="py-2 border-b border-slate-800/50 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              How It Works
            </a>
            <a
              href="#testimonials"
              className="py-2 border-b border-slate-800/50 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              Testimonials
            </a>
            <a
              href="#pricing"
              className="py-2 border-b border-slate-800/50 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </a>
            <Button
              className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-xl py-3"
              onClick={(e: any) => {
                // Add analytics
                window.location.href = "https://substack.com/@viralshortvids";
              }}
            >
              Join Newsletter
            </Button>
          </nav>
        </div>
      )}

      <main className="flex-1">
        {/* Hero Section with neumorphic elements and improved visual hierarchy */}
        <section className="py-24 px-6 lg:px-12 text-center relative overflow-hidden">
          {/* Abstract background elements */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-yellow-500/10 blur-3xl"></div>
          <div className="absolute top-40 -left-20 w-80 h-80 rounded-full bg-indigo-500/10 blur-3xl"></div>

          <div className="relative z-10 space-y-8 max-w-5xl mx-auto">
            <div className="inline-block px-4 py-1 bg-slate-800/50 rounded-full text-yellow-400 font-medium text-sm mb-4">
              Trending in 2025 • Video Marketing Revolution
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              Turn{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                Viral Videos
              </span>{" "}
              Into{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                Passive Income
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Discover the exact formats that are going viral on TikTok,
              Instagram Reels, and YouTube Shorts - and how to adapt them for
              your brand or personal account.
            </p>

            <div className="pt-8 flex flex-col md:flex-row items-center justify-center gap-6">
              <Button
                className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black text-lg px-8 py-6 rounded-xl font-bold shadow-lg shadow-yellow-500/20 transition-all duration-300 ease-in-out transform hover:scale-105 w-full md:w-auto"
                onClick={(e: any) => {
                  // Add analytics
                  window.location.href = "https://substack.com/@viralshortvids";
                }}
              >
                Get Weekly Viral Formats <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <a href="mailto://team@viralshortvids.com">
                <Button className="bg-slate-800/70 hover:bg-slate-700/70 text-white text-lg px-8 py-6 rounded-xl font-bold w-full md:w-auto backdrop-blur-sm">
                  Schedule Generator Demo
                </Button>
              </a>
            </div>

            <div className="flex flex-col md:flex-row justify-center gap-6 md:gap-10 pt-6 text-sm md:text-base">
              <div className="flex items-center justify-center">
                <div className="flex items-center justify-center bg-slate-800/30 p-3 rounded-full mr-3">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <span className="text-slate-300">Weekly Updates</span>
              </div>
              <div className="flex items-center justify-center">
                <div className="flex items-center justify-center bg-slate-800/30 p-3 rounded-full mr-3">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <span className="text-slate-300">Proven Formats</span>
              </div>
            </div>

            {/* Social proof */}
            <div className="mt-12 pt-6 border-t border-slate-800/50 max-w-3xl mx-auto">
              <p className="text-sm text-slate-400 mb-4">
                TRUSTED BY CREATORS WITH
              </p>
              <div className="flex flex-wrap justify-center gap-6 items-center">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded-sm mr-2"></div>
                  <span className="text-slate-300 font-medium">
                    5M+ YouTube Subscribers
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-pink-500 rounded-sm mr-2"></div>
                  <span className="text-slate-300 font-medium">
                    12M+ TikTok Followers
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded-sm mr-2"></div>
                  <span className="text-slate-300 font-medium">
                    8M+ Instagram Followers
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section with improved layout and neumorphic cards */}
        <section
          id="features"
          className="py-20 px-6 lg:px-12 bg-slate-950/50 relative"
        >
          <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-slate-950 to-transparent"></div>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-1 bg-slate-800/50 rounded-full text-yellow-400 font-medium text-sm mb-4">
                POWERFUL FEATURES
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                How to <span className="text-yellow-400">Make Money</span> with
                Viral Videos
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                Our platform provides everything you need to create, distribute,
                and monetize viral short-form videos across all major platforms.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="bg-slate-800/30 backdrop-blur-sm border-slate-700/30 text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:translate-y-[-8px] overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-amber-500"></div>
                <CardHeader>
                  <div className="p-3 bg-slate-700/50 rounded-xl w-fit mb-4">
                    <TrendingUp className="h-8 w-8 text-yellow-400" />
                  </div>
                  <CardTitle className="text-xl font-bold">
                    Weekly Viral Formats
                  </CardTitle>
                  <CardDescription className="text-slate-300">
                    Get the exact formats going viral right now
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">
                    Each week, we analyze thousands of viral videos across
                    TikTok, Instagram Reels, and YouTube Shorts to identify the
                    exact formats that are generating millions of views.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/30 backdrop-blur-sm border-slate-700/30 text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:translate-y-[-8px] overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-amber-500"></div>
                <CardHeader>
                  <div className="p-3 bg-slate-700/50 rounded-xl w-fit mb-4">
                    <Video className="h-8 w-8 text-yellow-400" />
                  </div>
                  <CardTitle className="text-xl font-bold">
                    AI Viral Template Generator
                  </CardTitle>
                  <CardDescription className="text-slate-300">
                    One-click access to customizable templates
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">
                    Turn any viral format we share into your own with our AI
                    template generator. Simply input your idea or let our AI
                    suggest viral concepts tailored to your niche or business.
                    Access your results through our Agent Chat UI powered by
                    LangChain, which enables chatting with our very own private
                    LangGraph server through an intuitive interface. We'll
                    generate and share the results with a video recording for
                    each template.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/30 backdrop-blur-sm border-slate-700/30 text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:translate-y-[-8px] overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-amber-500"></div>
                <CardHeader>
                  <div className="p-3 bg-slate-700/50 rounded-xl w-fit mb-4">
                    <DollarSign className="h-8 w-8 text-yellow-400" />
                  </div>
                  <CardTitle className="text-xl font-bold">
                    Monetization Strategies
                  </CardTitle>
                  <CardDescription className="text-slate-300">
                    Turn views into revenue streams
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">
                    Learn exactly how creators and brands are turning viral
                    videos into multiple income streams - from brand deals and
                    affiliate marketing to product sales and more.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Tabs */}
        <section id="pricing" className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-1 bg-slate-800/50 rounded-full text-yellow-400 font-medium text-sm mb-4">
              SIMPLE PRICING
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Choose Your <span className="text-yellow-400">Plan</span>
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto mb-10">
              Whether you're just starting out or scaling your content creation,
              we have a plan that fits your needs.
            </p>

            <div className="bg-slate-800/30 p-2 rounded-xl inline-flex mb-12">
              <button
                onClick={() => setActiveTab("weekly")}
                className={`px-6 py-3 rounded-lg font-medium ${
                  activeTab === "weekly"
                    ? "bg-yellow-500 text-black"
                    : "text-slate-300 hover:text-white"
                } transition-all duration-200`}
              >
                Weekly Newsletter
              </button>
              <button
                onClick={() => setActiveTab("premium")}
                className={`px-6 py-3 rounded-lg font-medium ${
                  activeTab === "premium"
                    ? "bg-yellow-500 text-black"
                    : "text-slate-300 hover:text-white"
                } transition-all duration-200`}
              >
                Premium
              </button>
            </div>

            {activeTab === "weekly" ? (
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-8 text-left border border-slate-700/30 shadow-xl">
                <div className="flex justify-between items-start flex-col md:flex-row">
                  <div>
                    <h3 className="text-2xl font-bold">Weekly Viral Formats</h3>
                    <p className="text-slate-300 mt-2">
                      Get weekly insights into what's trending
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <div className="text-4xl font-bold">
                      $0
                      <span className="text-lg text-slate-400 font-normal">
                        /month
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm">Free Forever</p>
                  </div>
                </div>

                <hr className="border-slate-700/50 my-6" />

                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
                    <p>
                      Weekly newsletter with viral video formats across all
                      major platforms
                    </p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
                    <p>Breakdowns of why these formats are working</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
                    <p>Basic insights on how to adapt formats for your niche</p>
                  </div>
                </div>

                <Button
                  className="mt-8 w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 rounded-xl"
                  onClick={(e: any) => {
                    // Add analytics
                    window.location.href =
                      "https://substack.com/@viralshortvids";
                  }}
                >
                  Subscribe to Newsletter
                </Button>
              </div>
            ) : (
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-8 text-left border border-slate-700/30 shadow-xl relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-40 h-40 bg-yellow-500/20 blur-3xl rounded-full"></div>
                <div className="absolute right-4 top-4 bg-yellow-500 text-black font-bold text-sm px-3 py-1 rounded-full">
                  POPULAR
                </div>

                <div className="flex justify-between items-start flex-col md:flex-row relative z-10">
                  <div>
                    <h3 className="text-2xl font-bold">Premium Access</h3>
                    <p className="text-slate-300 mt-2">
                      Everything you need to go viral
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <div className="text-4xl font-bold">
                      $4.99
                      <span className="text-lg text-slate-400 font-normal">
                        /month
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm">Billed monthly</p>
                  </div>
                </div>

                <hr className="border-slate-700/50 my-6" />

                <div className="space-y-4 relative z-10">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
                    <p>Everything in the free newsletter</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
                    <p>
                      <span className="font-bold">
                        Multi-Week Trend Reports
                      </span>{" "}
                      - Instead of receiving updates once a week, you will
                      receive 2+ updates per week instead.
                    </p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
                    <p>
                      <span className="font-bold">AI Template Generator</span> -
                      Receive custom templates based on the viral formats
                      reported for your specific needs. We'll our private
                      LangGraph server and the Agent Chat UI powered by
                      LangChain, to deliver you complete results (PDF file) with
                      video recordings on how it was generated.
                    </p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
                    <p>Access to private community of creators</p>
                  </div>
                </div>

                <Button
                  className="mt-8 w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-bold py-4 rounded-xl shadow-lg shadow-yellow-500/20"
                  onClick={(e: any) => {
                    // Add analytics
                    window.location.href =
                      "https://substack.com/@viralshortvids";
                  }}
                >
                  Get Premium Access
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* How It Works with visual improvements */}
        <section
          id="how-it-works"
          className="py-20 px-6 lg:px-12 bg-slate-950/50 relative"
        >
          <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-transparent to-slate-950/50"></div>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-1 bg-slate-800/50 rounded-full text-yellow-400 font-medium text-sm mb-4">
                EASY TO START
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                How <span className="text-yellow-400">ViralShortVids</span>{" "}
                Works
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                Follow these simple steps to start creating viral content that
                generates income.
              </p>
            </div>

            <div className="space-y-16 relative">
              {/* Connect the steps with a line */}
              <div className="absolute left-8 top-8 w-1 h-[calc(100%-64px)] bg-gradient-to-b from-yellow-500 to-slate-800 rounded-full hidden md:block"></div>

              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 relative z-10">
                  <div className="bg-slate-800 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold bg-gradient-to-br from-yellow-400 to-amber-500 text-black shadow-lg shadow-yellow-500/20">
                    1
                  </div>
                </div>
                <div className="bg-slate-800/30 backdrop-blur-sm p-6 rounded-xl border border-slate-700/30 flex-1">
                  <h3 className="text-xl font-bold mb-3">
                    Subscribe to Our Weekly Newsletter
                  </h3>
                  <p className="text-slate-300">
                    Every Monday, receive a curated list of this week's viral
                    video formats with detailed breakdowns of why they're
                    working. Our team analyzes thousands of trending videos to
                    identify patterns and opportunities.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 relative z-10">
                  <div className="bg-slate-800 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold bg-gradient-to-br from-yellow-400 to-amber-500 text-black shadow-lg shadow-yellow-500/20">
                    2
                  </div>
                </div>
                <div className="bg-slate-800/30 backdrop-blur-sm p-6 rounded-xl border border-slate-700/30 flex-1">
                  <h3 className="text-xl font-bold mb-3">
                    Adapt Viral Formats for Your Brand
                  </h3>
                  <p className="text-slate-300">
                    We show you exactly how these formats can be adapted for
                    different niches, businesses, and personal brands. Each
                    format comes with examples of successful adaptations across
                    multiple industries.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 relative z-10">
                  <div className="bg-slate-800 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold bg-gradient-to-br from-yellow-400 to-amber-500 text-black shadow-lg shadow-yellow-500/20">
                    3
                  </div>
                </div>
                <div className="bg-slate-800/30 backdrop-blur-sm p-6 rounded-xl border border-slate-700/30 flex-1">
                  <h3 className="text-xl font-bold mb-3">
                    Create & Monetize Your Content
                  </h3>
                  <p className="text-slate-300">
                    Use our AI template generator to create ready-to-use
                    templates based on viral formats. Access through our Agent
                    Chat UI powered by LangChain, allowing you to interact
                    directly with our private LangGraph server. Each template
                    comes with a complete video recording. Our platform provides
                    step-by-step monetization strategies to help you turn views
                    into revenue through brand deals, affiliate marketing,
                    product sales, and more.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials with improved design */}
        <section id="testimonials" className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-1 bg-slate-800/50 rounded-full text-yellow-400 font-medium text-sm mb-4">
                SUCCESS STORIES
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                From Our Community
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                See how creators and businesses are using ViralShortVids to grow
                their audience and generate income.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="bg-slate-800/30 backdrop-blur-sm border-slate-700/30 text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:translate-y-[-8px] overflow-hidden">
                <CardContent className="pt-6 relative">
                  <div className="absolute top-0 right-0 text-yellow-400 opacity-20">
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 6.5C11 9 9 11 6.5 11H5.5C5.5 16 8.5 18 11 18.5V17C8.5 16 8 14.5 8 13.5H9.5C12 13.5 14 11.5 14 9V6.5H11Z"
                        fill="currentColor"
                      />
                      <path
                        d="M21 6.5C21 9 19 11 16.5 11H15.5C15.5 16 18.5 18 21 18.5V17C18.5 16 18 14.5 18 13.5H19.5C22 13.5 24 11.5 24 9V6.5H21Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className="flex items-center mb-6">
                    <div className="mr-4">
                      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-black font-bold shadow-md">
                        JM
                      </div>
                    </div>
                    <div>
                      <p className="font-bold text-lg">Jamie Matthews</p>
                      <p className="text-sm text-slate-400">
                        Fitness Influencer
                      </p>
                    </div>
                  </div>
                  <p className="italic text-slate-300 relative z-10">
                    "After 6 months of stagnant growth, I used one of the viral
                    formats from the newsletter and gained 50k followers in just
                    two weeks. The template generator saved me hours of editing
                    time and helped me create consistent content."
                  </p>
                  <div className="mt-4 flex">
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/30 backdrop-blur-sm border-slate-700/30 text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:translate-y-[-8px] overflow-hidden">
                <CardContent className="pt-6 relative">
                  <div className="absolute top-0 right-0 text-yellow-400 opacity-20">
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 6.5C11 9 9 11 6.5 11H5.5C5.5 16 8.5 18 11 18.5V17C8.5 16 8 14.5 8 13.5H9.5C12 13.5 14 11.5 14 9V6.5H11Z"
                        fill="currentColor"
                      />
                      <path
                        d="M21 6.5C21 9 19 11 16.5 11H15.5C15.5 16 18.5 18 21 18.5V17C18.5 16 18 14.5 18 13.5H19.5C22 13.5 24 11.5 24 9V6.5H21Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className="flex items-center mb-6">
                    <div className="mr-4">
                      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-black font-bold shadow-md">
                        SC
                      </div>
                    </div>
                    <div>
                      <p className="font-bold text-lg">Sarah Chen</p>
                      <p className="text-sm text-slate-400">
                        E-commerce Business Owner
                      </p>
                    </div>
                  </div>
                  <p className="italic text-slate-300 relative z-10">
                    "I used the AI generator to create content for our small
                    jewelry business. One video hit 2M views on TikTok and drove
                    $14,000 in sales in a single weekend. The ROI has been
                    incredible, and now we have a dedicated content team using
                    these formats."
                  </p>
                  <div className="mt-4 flex">
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/30 backdrop-blur-sm border-slate-700/30 text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:translate-y-[-8px] overflow-hidden">
                <CardContent className="pt-6 relative">
                  <div className="absolute top-0 right-0 text-yellow-400 opacity-20">
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 6.5C11 9 9 11 6.5 11H5.5C5.5 16 8.5 18 11 18.5V17C8.5 16 8 14.5 8 13.5H9.5C12 13.5 14 11.5 14 9V6.5H11Z"
                        fill="currentColor"
                      />
                      <path
                        d="M21 6.5C21 9 19 11 16.5 11H15.5C15.5 16 18.5 18 21 18.5V17C18.5 16 18 14.5 18 13.5H19.5C22 13.5 24 11.5 24 9V6.5H21Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className="flex items-center mb-6">
                    <div className="mr-4">
                      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-black font-bold shadow-md">
                        MJ
                      </div>
                    </div>
                    <div>
                      <p className="font-bold text-lg">Marcus Johnson</p>
                      <p className="text-sm text-slate-400">
                        Digital Marketing Agency
                      </p>
                    </div>
                  </div>
                  <p className="italic text-slate-300 relative z-10">
                    "We've incorporated ViralShortVids into our client services
                    and have seen engagement increase by 320% on average. Our
                    clients love the results, and we've been able to charge
                    premium rates for viral content creation."
                  </p>
                  <div className="mt-4 flex">
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                    <Star className="h-4 w-4 text-yellow-400" fill="yellow" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Testimonial stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 text-center border border-slate-700/30">
                <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                  92%
                </div>
                <p className="text-slate-300">
                  of customers saw follower growth within 30 days
                </p>
              </div>
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 text-center border border-slate-700/30">
                <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                  78%
                </div>
                <p className="text-slate-300">
                  increase in engagement across all platforms
                </p>
              </div>
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 text-center border border-slate-700/30">
                <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                  12.4M
                </div>
                <p className="text-slate-300">
                  total views generated for our customers in the last month
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Modern, neumorphic CTA Section */}
        <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-12 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-slate-900 to-black"></div>
          <div className="absolute -top-20 sm:-top-40 -right-20 sm:-right-40 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 rounded-full bg-yellow-500/10 blur-3xl"></div>
          <div className="absolute -bottom-20 sm:-bottom-40 -left-20 sm:-left-40 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 rounded-full bg-yellow-500/10 blur-3xl"></div>

          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="bg-slate-800/30 backdrop-blur-lg rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 border border-slate-700/30 shadow-xl sm:shadow-2xl">
              <div className="inline-block px-3 sm:px-4 py-1 bg-yellow-500/20 rounded-full text-yellow-400 font-medium text-xs sm:text-sm mb-4 sm:mb-6">
                LIMITED TIME OFFER
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
                Ready to Go Viral and{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                  Generate Passive Income?
                </span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
                Join thousands of creators and businesses who are leveraging
                viral video formats to grow their audience and revenue.
              </p>
              <div className="flex flex-col sm:flex-col gap-4 justify-center w-full max-w-2xl mx-auto">
                <Button
                  className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black text-base sm:text-lg px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 rounded-xl font-bold shadow-lg shadow-yellow-500/20 transition-all duration-300 ease-in-out transform hover:scale-105 w-full flex items-center justify-center"
                  onClick={(e) => {
                    // Add analytics
                    window.location.href =
                      "https://substack.com/@viralshortvids";
                  }}
                >
                  <span>Subscribe to Weekly Viral Formats</span>{" "}
                </Button>
                <a
                  href="mailto://team@viralshortvids.com"
                  className="w-full sm:w-auto"
                >
                  <Button className="bg-slate-700/50 hover:bg-slate-600/50 text-white text-base sm:text-lg px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 rounded-xl font-bold backdrop-blur-sm w-full">
                    Schedule Generator Demo
                  </Button>
                </a>
              </div>
              <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-slate-400">
                No credit card required for newsletter. Premium features just
                $4.99/month.
              </p>

              {/* Trust badges */}
              <div className="mt-8 sm:mt-10 md:mt-12 pt-6 sm:pt-8 border-t border-slate-700/30">
                <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4">
                  TRUSTED BY SOCIAL MEDIA PROFESSIONALS
                </p>
                <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 items-center opacity-70">
                  <div className="h-6 sm:h-8">
                    <svg
                      width="100"
                      height="28"
                      viewBox="0 0 120 32"
                      className="w-24 sm:w-28 md:w-32 h-auto"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        width="120"
                        height="32"
                        rx="4"
                        fill="#D1D5DB"
                        fillOpacity="0.1"
                      />
                      <text
                        x="60"
                        y="20"
                        fontFamily="Arial"
                        fontSize="12"
                        fill="#D1D5DB"
                        textAnchor="middle"
                      >
                        TikTok Partners
                      </text>
                    </svg>
                  </div>
                  <div className="h-6 sm:h-8">
                    <svg
                      width="120"
                      height="28"
                      viewBox="0 0 140 32"
                      className="w-28 sm:w-32 md:w-36 h-auto"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        width="140"
                        height="32"
                        rx="4"
                        fill="#D1D5DB"
                        fillOpacity="0.1"
                      />
                      <text
                        x="70"
                        y="20"
                        fontFamily="Arial"
                        fontSize="12"
                        fill="#D1D5DB"
                        textAnchor="middle"
                      >
                        Meta Business Partners
                      </text>
                    </svg>
                  </div>
                  <div className="h-6 sm:h-8">
                    <svg
                      width="140"
                      height="28"
                      viewBox="0 0 160 32"
                      className="w-32 sm:w-36 md:w-40 h-auto"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        width="160"
                        height="32"
                        rx="4"
                        fill="#D1D5DB"
                        fillOpacity="0.1"
                      />
                      <text
                        x="80"
                        y="20"
                        fontFamily="Arial"
                        fontSize="12"
                        fill="#D1D5DB"
                        textAnchor="middle"
                      >
                        YouTube Content Solutions
                      </text>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-12 px-6 lg:px-12 border-t border-slate-800/50 bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-slate-800/40 rounded-xl shadow-lg">
                  <Zap className="h-6 w-6 text-yellow-400" />
                </div>
                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                  ViralShortVids
                </span>
              </div>
              <p className="text-slate-400 mb-6">
                Helping creators and businesses turn viral video formats into
                passive income streams.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">Resources</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="https://substack.com/@viralshortvids"
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    Weekly Newsletter
                  </a>
                </li>
                <li>
                  <a
                    href="https://substack.com/@viralshortvids"
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    AI Viral Template Generator
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">Company</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="mailto://team@viralshortvids.com"
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <a
                    href="mailto://team@viralshortvids.com"
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    Partners
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800/50 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-slate-500 mb-4 md:mb-0">
              © 2025 ViralShortVids. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
